import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import { REACT_APP_API_BASE_URL } from "../../config";
import { ImagePreviewDialog, ClickableImage } from "./ImagePreview";
import { ProductsTableHeader } from "./TableComponents";

const NotFoundProducts = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [authUser, setAuthUser] = useState(undefined);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authUser === undefined) return;
    if (!authUser) {
      setLoading(false);
      setError("User not authenticated");
      return;
    }
    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await authUser.getIdToken();
        const res = await axios.get(
          `${REACT_APP_API_BASE_URL}/report/not_found`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        setReports(arr);
      } catch (err) {
        setError(err.message || "Failed to fetch not found reports");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [authUser]);

  // Fetch all image URLs on reports load
  const [imageUrls, setImageUrls] = useState({});
  useEffect(() => {
    const fetchAllImageUrls = async () => {
      if (!reports.length) return;
      if (!authUser) return;
      const urls = {};
      reports.forEach((row) => {
        if (row.image_url) {
          urls[row.image_url] = row.image_url;
        }
      });
      setImageUrls(urls);
    };
    fetchAllImageUrls();
  }, [reports, authUser]);

  // Handle image click to fetch and preview the image
  const handleImageClick = async (imageKey) => {
    try {
      setPreviewImage(imageKey);
      setPreviewOpen(true);
    } catch (err) {
      setError("Failed to load image");
    }
  };

  // Helper to format date time
  const formatDateTime = (epochMicroseconds) => {
    if (!epochMicroseconds) return "";
    const d = new Date(parseInt(epochMicroseconds) / 1000);
    if (isNaN(d)) return epochMicroseconds;
    const pad = (n) => n.toString().padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    const mm = pad(d.getUTCMonth() + 1);
    const dd = pad(d.getUTCDate());
    const hh = pad(d.getUTCHours());
    const min = pad(d.getUTCMinutes());
    const ss = pad(d.getUTCSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ flexGrow: 1, fontWeight: "bold" }}
        >
          Not Found Reports
        </Typography>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <ProductsTableHeader type="notfound" />
            <TableBody>
              {reports.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.user_email}</TableCell>
                  <TableCell>{formatDateTime(row.created_time)}</TableCell>
                  <TableCell>
                    {row.image_url &&
                      (imageUrls[row.image_url] ? (
                        <ClickableImage
                          src={imageUrls[row.image_url]}
                          alt="Preview"
                          onClick={() => handleImageClick(row.image_url)}
                        />
                      ) : (
                        <Typography color="error" variant="body2">
                          Image not found
                        </Typography>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewImage(null);
        }}
        imageUrl={previewImage}
      />
    </Box>
  );
};

export default NotFoundProducts;
